import { useContext } from "react";
import { useFormSubmission } from "@msidentity/SISU/components/inputs/hooks/use-form-submission";
import { useInput } from "@msidentity/SISU/components/inputs/hooks/use-input";
import { OneTimeTokenRegex } from "@msidentity/SISU/components/inputs/input-regex-constants";
import { ViewId } from "@msidentity/SISU/constants";
import { useNavigateBack, useRedirect } from "@msidentity/SISU/hooks";
import { getValidationMethod } from "@msidentity/SISU/utilities/input-helper";
import { isValidOtt } from "@msidentity/SISU/utilities/validators/ott-validator";
import {
  usernameRecoveryEnterCodeViewStringsFabric,
  usernameRecoveryGenericStringsFabric,
} from "../fabric/username-recovery-strings-fabric";
import UsernameRecoveryConfig from "../username-recovery-config";
import { UsernameRecoveryContext } from "../username-recovery-context";
import { useGetAccountsForProof } from "./use-get-accounts-for-proof";

/**
 * @returns handler for calling SendOTT.
 */
export const useUsernameRecoveryEnterCode = function useUsernameRecoveryEnterCode() {
  const { cancelUrl, bypassClientProofValidationWithZero } = UsernameRecoveryConfig.instance;
  const inputValidationMethod = getValidationMethod(
    (value: string) => isValidOtt(value, bypassClientProofValidationWithZero, OneTimeTokenRegex),
    usernameRecoveryEnterCodeViewStringsFabric.errorInvalidCode,
    usernameRecoveryEnterCodeViewStringsFabric.emptyCodeError,
  );

  const inputPropsForOTT = useInput({
    hasInitialFocus: true,
    validationMethod: inputValidationMethod,
  });

  const navigateBack = useNavigateBack();

  const {
    dispatchStateChange,
    viewState: { phoneCountryState, usernameType, usernameVal, usernameDisplay, hasPrefill },
  } = useContext(UsernameRecoveryContext);

  const redirect = useRedirect();
  const onCancelHandler = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (cancelUrl) {
      redirect(cancelUrl);
    }
  };

  const submitTask = useGetAccountsForProof(
    usernameVal,
    inputPropsForOTT.value,
    phoneCountryState.iso,
    usernameType,
    dispatchStateChange,
  );
  const onSubmitOneTimeCode = useFormSubmission({
    inputState: inputPropsForOTT,
    viewId: ViewId.UsernameRecoveryLegacyCode,
    submitTask,
  });

  const secondaryButtonText = hasPrefill
    ? usernameRecoveryGenericStringsFabric.back
    : usernameRecoveryGenericStringsFabric.cancel;

  return {
    inputProps: inputPropsForOTT,
    onCancelHandler,
    onSubmit: onSubmitOneTimeCode,
    onBack: navigateBack,
    usernameDisplay,
    displaySwitchLink: !hasPrefill,
    secondaryButtonText,
  };
};
